import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { Landing } from "../components/Landing";
import { landingHead } from "../lib/marketing/head";
import {
  langPath,
  loadMarketingBundle,
  siteLangOf,
} from "../lib/marketing/registry";
import { documentHeaders } from "../lib/seo";

/**
 * The landing page in the other eight languages: `/es`, `/pt`, `/fr`, `/de`,
 * `/it`, `/ru`, `/ko`, `/tl`.
 *
 * ── Why this does not swallow the rest of the site ───────────────────────────
 *
 * A one-segment dynamic route sits at the same depth as `/privacy`, `/terms`,
 * `/support` and `/delete`. TanStack Router ranks a static segment above a
 * dynamic one, so those four keep matching themselves; only paths that match
 * nothing else arrive here.
 *
 * ── Why an unknown segment 404s ──────────────────────────────────────────────
 *
 * `/zz` could render English and be helpful. It would also mean every typo
 * anyone ever links is a live URL serving the same page as `/` — an unbounded
 * supply of duplicates, all of them canonicalising to a page that already
 * exists, on the one route where thin duplicate content costs the most. So an
 * unrecognised segment is a 404, which is what it actually is.
 *
 * ── Why a segment that resolves to a DIFFERENT url is a 301 ──────────────────
 *
 * `siteLangOf` is forgiving in ways the URL space must not be: it lowercases,
 * it takes the primary subtag, and it maps Apple's `fil` onto `tl`. So `/ES`,
 * `/es-MX`, `/Fil` and `/en` all resolve to a real language — and all served
 * a 200 with a canonical pointing somewhere else, which is a live duplicate URL
 * for every case permutation and locale tag anyone might link.
 *
 * The rule is therefore not "is this a language" but "is this THE url for that
 * language": anything whose canonical path differs from what was asked for is
 * a permanent redirect to that path. `/en` falls out of the same rule for free,
 * since English lives at the root.
 */
export const Route = createFileRoute("/$lang")({
  loader: ({ params }) => {
    const lang = siteLangOf(params.lang);
    if (!lang) throw notFound();
    const path = langPath(lang);
    if (path !== `/${params.lang}`)
      throw redirect({ href: path, statusCode: 301 });
    return loadMarketingBundle(lang).then((bundle) => ({ lang, bundle }));
  },
  headers: documentHeaders,
  head: ({ loaderData }) =>
    loaderData ? landingHead(loaderData.lang, loaderData.bundle) : {},
  component: LocalizedHome,
});

function LocalizedHome() {
  const { lang, bundle } = Route.useLoaderData();
  return <Landing bundle={bundle} lang={lang} />;
}
